import { useContext, useEffect, useState } from "react";
import { StudentContext } from "../../contexts/studentContext";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Breadcrumb,
  Pagination,
  Form,
  Card,
  Row,
  Col,
  Table,
  Badge,
  InputGroup,
  Button,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import {
  Search,
  PersonBadge,
  Calendar3,
  ArrowClockwise,
  List,
} from "react-bootstrap-icons";
import { useAuth } from "@clerk/clerk-react";
import createAxiosInstance from "../utility/utils";

const themeColor = "rgb(79, 81, 163)";

export default function ListStudents() {
  const { students, studentsDispatch } = useContext(StudentContext);
  const [batches, setBatches] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedStudentBatches, setSelectedStudentBatches] = useState([]);
  const [selectedStudentName, setSelectedStudentName] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  const navigate = useNavigate();

  const { getToken } = useAuth();
  const axiosInstance = createAxiosInstance(getToken);

  useEffect(() => {
    (async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setActionError("Error loading data. Please refresh the page.");
      }
    })();
  }, []);

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const response = await axiosInstance.get(`/api/batches`);
        const batchData = response.data.reduce((acc, batch) => {
          acc[batch._id] = batch.name;
          return acc;
        }, {});
        setBatches(batchData);
      } catch (err) {
        setActionError("Failed to load batch information.");
      }
    };

    fetchBatches();
  }, []);

  useEffect(() => {
    if (students?.data) {
      const filtered = students.data.filter(
        (student) =>
          student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.usnNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
      setCurrentPage(1);
    }
  }, [searchTerm, students]);

  const handleView = (studentId) => {
    navigate(`/attendance/${studentId}`);
  };

  const handleDelete = async (id) => {
    const userConfirm = window.confirm(
      "Are you sure you want to delete this student? This action cannot be undone."
    );
    if (userConfirm) {
      setDeleteLoading(id);
      setActionError("");
      try {
        await axiosInstance.delete(`/api/students/${id}`);
        studentsDispatch({ type: "REMOVE_STUDENT", payload: id });
        setActionSuccess("Student deleted successfully");
        setTimeout(() => setActionSuccess(""), 3000);
      } catch (err) {
        setActionError("Failed to delete student. Please try again.");
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  const showBatchDetails = (student) => {
    const batchList = Array.isArray(student.batch)
      ? student.batch
      : [student.batch];
    setSelectedStudentBatches(batchList);
    setSelectedStudentName(student.name);
    setShowBatchModal(true);
  };

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPaginationItems = () => {
    let items = [];

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
      />
    );

    if (currentPage > 3) {
      items.push(
        <Pagination.Item key={1} onClick={() => paginate(1)}>
          1
        </Pagination.Item>
      );
      if (currentPage > 4) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    const startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, currentPage + 1);

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => paginate(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (currentPage < totalPages - 2) {
      if (currentPage < totalPages - 3) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => paginate(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );

    return items;
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const handleAddStudent = () => {
    navigate("/admin-dashboard");
  };

  if (isLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <Card className="shadow p-5 text-center">
          <Spinner
            animation="border"
            style={{ color: themeColor }}
            className="mx-auto"
          />
          <h5 className="mt-3 text-muted">Loading Students...</h5>
          <p className="text-muted">Please wait while we fetch the data</p>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Breadcrumb className="mb-4">
        <Breadcrumb.Item href="/admin-dashboard">Home</Breadcrumb.Item>
        <Breadcrumb.Item active>Students List</Breadcrumb.Item>
      </Breadcrumb>

      {actionError && (
        <Alert variant="danger" onClose={() => setActionError("")} dismissible>
          {actionError}
        </Alert>
      )}

      {actionSuccess && (
        <Alert
          variant="success"
          onClose={() => setActionSuccess("")}
          dismissible
        >
          {actionSuccess}
        </Alert>
      )}

      <Card className="shadow mb-4">
        <Card.Header className="bg-white">
          <Row className="align-items-center">
            <Col
              xs={12}
              className="d-flex justify-content-between align-items-center flex-wrap"
            >
              <h4 className="mb-0 d-flex align-items-center">
                <PersonBadge className="me-2" style={{ color: themeColor }} />
                Students Directory
              </h4>
              <div className="mt-2 mt-md-0">
                <Button
                  style={{
                    backgroundColor: themeColor,
                    borderColor: themeColor,
                  }}
                  onClick={handleAddStudent}
                >
                  Add Student
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Form className="mb-4">
            <InputGroup>
              <InputGroup.Text>
                <Search />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name, USN, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button variant="outline-secondary" onClick={clearSearch}>
                  Clear
                </Button>
              )}
            </InputGroup>
          </Form>

          <div className="table-responsive">
            <Table hover bordered className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="text-center" style={{ width: "5%" }}>
                    #
                  </th>
                  <th style={{ width: "25%" }}>Student Name</th>
                  <th style={{ width: "15%" }}>USN</th>
                  <th style={{ width: "25%" }}>Email</th>
                  <th style={{ width: "15%" }}>Batch</th>
                  <th className="text-center" style={{ width: "15%" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentStudents.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <div className="text-muted">
                        <ArrowClockwise className="mb-2" size={24} />
                        <p>No students found matching your search criteria</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentStudents.map((student, i) => (
                    <tr key={student._id}>
                      <td className="text-center">
                        {indexOfFirstStudent + i + 1}
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex justify-content-center align-items-center me-2"
                            style={{
                              width: "32px",
                              height: "32px",
                              fontSize: "12px",
                              color: "white",
                              backgroundColor: themeColor,
                            }}
                          >
                            {student.name
                              .split(" ")
                              .map((name) => name[0])
                              .join("")
                              .toUpperCase()}
                          </div>
                          <div>{student.name}</div>
                        </div>
                      </td>
                      <td>{student.usnNumber}</td>
                      <td>{student.email}</td>
                      <td>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => showBatchDetails(student)}
                          className="d-flex align-items-center"
                          style={{
                            borderColor: themeColor,
                            color: themeColor,
                          }}
                        >
                          <List className="me-1" />
                          View Batches
                          {Array.isArray(student.batch) && (
                            <Badge
                              pill
                              className="ms-1"
                              style={{
                                backgroundColor: themeColor,
                              }}
                            >
                              {student.batch.length}
                            </Badge>
                          )}
                        </Button>
                      </td>
                      <td className="text-center">
                        <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                          <Button
                            size="sm"
                            style={{
                              borderColor: themeColor,
                              color: themeColor,
                            }}
                            variant="outline"
                            onClick={() => handleView(student._id)}
                          >
                            <Calendar3 className="me-1" />
                            <span className="d-none d-sm-inline">
                              Attendance
                            </span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(student._id)}
                            disabled={deleteLoading === student._id}
                          >
                            {deleteLoading === student._id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              "Delete"
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination with responsive design */}
          {filteredStudents.length > 0 && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4">
              <small className="text-muted mb-3 mb-md-0">
                Showing{" "}
                {currentStudents.length > 0 ? indexOfFirstStudent + 1 : 0} to{" "}
                {Math.min(indexOfLastStudent, filteredStudents.length)} of{" "}
                {filteredStudents.length} entries
              </small>

              <Pagination className="flex-wrap mb-0 justify-content-center">
                {getPaginationItems()}
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Batch Details Modal */}
      <Modal
        show={showBatchModal}
        onHide={() => setShowBatchModal(false)}
        centered
        size="sm"
      >
        <Modal.Header
          closeButton
          style={{ borderBottom: `3px solid ${themeColor}` }}
        >
          <Modal.Title as="h5">Batches for {selectedStudentName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {selectedStudentBatches.length === 0 ? (
            <p className="text-muted text-center">No batches assigned</p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {selectedStudentBatches.map((batchId, index) => (
                <Badge
                  key={index}
                  pill
                  style={{
                    backgroundColor: themeColor,
                    color: "#fff",
                    fontSize: "0.9rem",
                  }}
                >
                  {batches[batchId] || "Unknown Batch"}
                </Badge>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="p-2">
          <Button
            size="sm"
            onClick={() => setShowBatchModal(false)}
            style={{
              backgroundColor: themeColor,
              borderColor: themeColor,
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
